import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Maximize, Bed, Bath, ArrowRight, ArrowDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_VIDEO = '/building-video.mp4';
const HERO_POSTER = '/building-poster.jpg';

const TOWER_NAMES = { kirei1: 'Kirei I', kirei2: 'Kirei II' };

const statusColors = {
  available: '#2E7D32',
  reserved: '#D4AF37',
  sold: '#C62828',
};

const statusBg = {
  available: 'rgba(46, 125, 50, 0.15)',
  reserved: 'rgba(212, 175, 55, 0.15)',
  sold: 'rgba(198, 40, 40, 0.15)',
};

const FLOOR_HEIGHT = 7;

function generateFloorPositions(totalFloors) {
  const positions = [];
  for (let i = 0; i < totalFloors; i++) {
    positions.push({
      floor: totalFloors - i,
      top: i * (76.5 / Math.max(totalFloors - 1, 1)),
    });
  }
  return positions;
}

function clamp(v, min, max) { return Math.min(Math.max(v, min), max); }
function mapRange(value, inMin, inMax, outMin, outMax) {
  return outMin + ((clamp(value, inMin, inMax) - inMin) / (inMax - inMin)) * (outMax - outMin);
}

export default function HomePage() {
  const { towerId } = useParams();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const explorerRef = useRef(null);
  const videoRef = useRef(null);
  const [apartments, setApartments] = useState([]);
  const [hoveredFloor, setHoveredFloor] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [totalFloors, setTotalFloors] = useState(10);

  const towerName = TOWER_NAMES[towerId] || towerId;
  const floorPositions = generateFloorPositions(totalFloors);

  useEffect(() => {
    axios.get(`${API}/apartments?tower=${towerId}`).then(res => setApartments(res.data)).catch(() => {});
    axios.get(`${API}/towers`).then(res => {
      const tw = res.data.find(t => t.id === towerId);
      if (tw) setTotalFloors(tw.total_floors);
    }).catch(() => {});
  }, [towerId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const tryPlay = () => { video.play().catch(() => {}); };
    tryPlay();
    const events = ['touchstart', 'scroll', 'click'];
    const onInteraction = () => {
      tryPlay();
      events.forEach(e => document.removeEventListener(e, onInteraction));
    };
    events.forEach(e => document.addEventListener(e, onInteraction, { once: true, passive: true }));
    return () => events.forEach(e => document.removeEventListener(e, onInteraction));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const totalScrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = totalScrollable > 0 ? window.scrollY / totalScrollable : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToExplorer = useCallback(() => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  }, []);

  const imgScale = mapRange(scrollProgress, 0, 1, 1.0, 1.9);
  const imgTranslateY = mapRange(scrollProgress, 0.35, 1, 0, -3);
  const heroOpacity = mapRange(scrollProgress, 0.15, 0.35, 1, 0);
  const explorerOpacity = mapRange(scrollProgress, 0.45, 0.65, 0, 1);
  const overlayDarkness = mapRange(scrollProgress, 0, 0.6, 0.15, 0.4);

  const heroHidden = heroOpacity < 0.01;
  const explorerShown = explorerOpacity > 0.1;

  const getFloorData = (floor) => apartments.filter(a => a.floor === floor);

  const statusLabel = (s) => ({
    available: lang === 'es' ? 'Disponible' : 'Available',
    reserved: lang === 'es' ? 'Reservado' : 'Reserved',
    sold: lang === 'es' ? 'Vendido' : 'Sold',
  }[s] || s);

  const formatPrice = (p) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(p);

  const activeFloor = selectedFloor || hoveredFloor;
  const activeData = activeFloor ? getFloorData(activeFloor) : [];

  return (
    <div data-testid="home-page" className="relative bg-black" style={{ height: '300vh' }}>

      {/* === FIXED VIDEO/IMAGE BACKGROUND === */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        <video
          ref={videoRef}
          autoPlay muted loop playsInline preload="auto"
          poster={HERO_POSTER}
          className="absolute w-full h-full object-cover will-change-transform"
          style={{
            transform: `scale(${imgScale}) translateY(${imgTranslateY}%)`,
            transformOrigin: '62% 42%',
            transition: 'transform 0.08s linear',
          }}
          data-testid="building-render-video"
        >
          <source src={`${HERO_VIDEO}?v=2`} type="video/mp4" />
        </video>
        <div
          className="absolute inset-0 bg-black transition-opacity duration-100"
          style={{ opacity: overlayDarkness }}
        />
      </div>

      {/* === HERO === */}
      <div
        className="fixed inset-0 z-10 flex flex-col items-center justify-center px-6"
        style={{
          opacity: heroOpacity,
          visibility: heroHidden ? 'hidden' : 'visible',
          pointerEvents: heroHidden ? 'none' : 'auto',
          transition: 'opacity 0.1s linear',
        }}
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-xs font-bold uppercase tracking-[0.3em] text-white/60 mb-8"
          style={{ fontFamily: "'Outfit', sans-serif" }}
          data-testid="hero-overline"
        >
          {lang === 'es' ? 'CERRO DE LA PLATA PRESENTA' : 'CERRO DE LA PLATA PRESENTS'}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="text-6xl sm:text-7xl lg:text-9xl font-light tracking-[-0.02em] mb-6 gold-text"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
          data-testid="hero-title"
        >
          {towerName}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="text-base md:text-lg text-white/70 font-light max-w-xl text-center mb-12 leading-relaxed"
          style={{ fontFamily: "'Outfit', sans-serif" }}
          data-testid="hero-subtitle"
        >
          {lang === 'es'
            ? 'Un nuevo concepto de vida en altura. Exclusividad, diseño y confort.'
            : 'A new concept of elevated living. Exclusivity, design and comfort.'}
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          onClick={scrollToExplorer}
          data-testid="hero-cta"
          className="bg-[#D4AF37] text-white px-10 py-4 uppercase tracking-[0.15em] text-sm font-light hover:bg-[#B5952F] transition-all duration-300 border border-[#D4AF37]"
        >
          {lang === 'es' ? 'Explorar Disponibilidad' : 'Explore Availability'}
        </motion.button>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.8 }}
          className="absolute bottom-12 flex flex-col items-center gap-3 cursor-pointer"
          onClick={scrollToExplorer}
        >
          <span className="text-white/40 text-xs tracking-[0.2em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {lang === 'es' ? 'Desplaza para descubrir' : 'Scroll to discover'}
          </span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            <ArrowDown size={18} className="text-[#D4AF37]" />
          </motion.div>
        </motion.div>
      </div>

      {/* === EXPLORER OVERLAY === */}
      <div
        className="fixed inset-0 z-20"
        style={{
          opacity: explorerOpacity,
          visibility: explorerShown ? 'visible' : 'hidden',
          pointerEvents: explorerShown ? 'auto' : 'none',
          transition: 'opacity 0.1s linear',
        }}
      >
        {/* ===== DESKTOP LAYOUT (lg+) ===== */}
        <div className="hidden lg:flex w-full h-full">
          <div className="flex-1 relative pt-24">
            <div
              className="absolute"
              style={{ top: '18%', left: '30%', width: '28%', height: '72%' }}
            >
              {floorPositions.map(({ floor, top }) => {
                const units = getFloorData(floor);
                const isActive = activeFloor === floor;
                const isCompact = units.length > 3;

                return (
                  <div
                    key={floor}
                    data-testid={`floor-${floor}`}
                    className="absolute left-0 right-0 cursor-pointer"
                    style={{ top: `${top}%`, height: `${FLOOR_HEIGHT}%` }}
                    onMouseEnter={() => setHoveredFloor(floor)}
                    onMouseLeave={() => setHoveredFloor(null)}
                    onClick={() => setSelectedFloor(prev => prev === floor ? null : floor)}
                  >
                    <div
                      className="absolute inset-0 border-y transition-all duration-300"
                      style={{
                        background: isActive
                          ? 'linear-gradient(90deg, rgba(212,175,55,0.28), rgba(212,175,55,0.12), rgba(212,175,55,0.04))'
                          : 'transparent',
                        borderColor: isActive ? 'rgba(212,175,55,0.45)' : 'transparent',
                      }}
                    />
                    <div
                      className="absolute right-full top-1/2 -translate-y-1/2 mr-3 transition-opacity duration-300"
                      style={{ opacity: isActive ? 1 : 0.75 }}
                    >
                      <span
                        className="whitespace-nowrap text-[11px] tracking-[0.08em] uppercase px-2.5 py-1"
                        style={{
                          fontFamily: "'Outfit', sans-serif",
                          color: isActive ? '#D4AF37' : 'rgba(255,255,255,0.85)',
                          background: isActive ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.5)',
                          backdropFilter: 'blur(6px)',
                        }}
                      >
                        {floor >= 9 ? 'PH ' : ''}{lang === 'es' ? 'Piso' : 'Floor'} {floor}
                      </span>
                    </div>
                    <div
                      className="absolute left-full top-1/2 -translate-y-1/2 ml-3 flex items-center gap-2 transition-all duration-300 px-2 py-1"
                      style={{
                        opacity: isActive ? 1 : 0.65,
                        background: isActive ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.35)',
                        backdropFilter: 'blur(6px)',
                      }}
                    >
                      {isCompact ? (
                        <div className="flex flex-wrap gap-1 max-w-[100px]">
                          {units.map(u => (
                            <div
                              key={u.id}
                              className="w-2 h-2 rounded-full transition-all duration-300"
                              style={{
                                backgroundColor: statusColors[u.status],
                                boxShadow: isActive ? `0 0 6px ${statusColors[u.status]}80` : 'none',
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        units.map(u => (
                          <div key={u.id} className="flex items-center gap-1">
                            <div
                              className="w-2.5 h-2.5 rounded-full transition-all duration-300"
                              style={{
                                backgroundColor: statusColors[u.status],
                                boxShadow: isActive ? `0 0 10px ${statusColors[u.status]}80` : 'none',
                              }}
                            />
                            <span className="text-[9px] text-white/50 font-light">{u.unit}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="absolute bottom-8 left-8 flex gap-5">
              {[
                { key: 'available', label: lang === 'es' ? 'Disponible' : 'Available', color: statusColors.available },
                { key: 'reserved', label: lang === 'es' ? 'Reservado' : 'Reserved', color: statusColors.reserved },
                { key: 'sold', label: lang === 'es' ? 'Vendido' : 'Sold', color: statusColors.sold },
              ].map(item => (
                <div key={item.key} className="flex items-center gap-2" data-testid={`legend-${item.key}`}>
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-[10px] text-white/40 tracking-[0.1em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Info panel */}
          <div className="w-[380px] flex-shrink-0 h-full flex flex-col pr-8 pl-4 pt-24">
            <div className="mb-6">
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {towerName}
              </p>
              <h2 className="text-2xl font-light tracking-tight gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="building-title">
                {lang === 'es' ? 'Selecciona un Piso' : 'Select a Floor'}
              </h2>
            </div>
            <div className="flex-1 flex flex-col justify-start pt-4 overflow-y-auto max-h-[calc(100vh-180px)]">
              <InfoPanel
                towerId={towerId}
                activeFloor={activeFloor}
                activeData={activeData}
                lang={lang}
                statusColors={statusColors}
                statusBg={statusBg}
                statusLabel={statusLabel}
                formatPrice={formatPrice}
                navigate={navigate}
              />
            </div>
          </div>
        </div>

        {/* ===== MOBILE LAYOUT (<lg) ===== */}
        <div className="flex lg:hidden flex-col w-full h-full">
          <div className="pt-18 px-4 pb-2 flex-shrink-0">
            <p className="text-[9px] tracking-[0.2em] uppercase text-white/40" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {towerName}
            </p>
            <h2 className="text-lg font-light tracking-tight gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="building-title-mobile">
              {lang === 'es' ? 'Selecciona un Piso' : 'Select a Floor'}
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1.5">
            {floorPositions.map(({ floor }) => {
              const units = getFloorData(floor);
              const isActive = activeFloor === floor;
              const isCompact = units.length > 3;

              return (
                <button
                  key={floor}
                  data-testid={`floor-mobile-${floor}`}
                  onClick={() => setSelectedFloor(prev => prev === floor ? null : floor)}
                  className="w-full flex items-center h-12 px-4 border transition-all duration-200 text-left"
                  style={{
                    background: isActive ? 'rgba(212, 175, 55, 0.15)' : 'rgba(255,255,255,0.05)',
                    borderColor: isActive ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <span
                    className="text-xl w-10 font-light"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: isActive ? '#D4AF37' : 'rgba(255,255,255,0.6)' }}
                  >
                    {floor}
                  </span>
                  <span
                    className="text-[10px] tracking-[0.08em] uppercase flex-1"
                    style={{ fontFamily: "'Outfit', sans-serif", color: isActive ? '#fff' : 'rgba(255,255,255,0.5)' }}
                  >
                    {floor >= 9 ? 'PH — ' : ''}{lang === 'es' ? 'Piso' : 'Floor'} {floor}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {isCompact ? (
                      <div className="flex flex-wrap gap-1 max-w-[80px]">
                        {units.map(u => (
                          <div key={u.id} className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors[u.status] }} />
                        ))}
                      </div>
                    ) : (
                      units.map(u => (
                        <div key={u.id} className="flex items-center gap-1">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: statusColors[u.status] }} />
                          <span className="text-[9px] text-white/40">{u.unit}</span>
                        </div>
                      ))
                    )}
                  </div>
                </button>
              );
            })}

            <div className="flex gap-4 pt-2 pb-1">
              {[
                { key: 'available', label: lang === 'es' ? 'Disponible' : 'Available', color: statusColors.available },
                { key: 'reserved', label: lang === 'es' ? 'Reservado' : 'Reserved', color: statusColors.reserved },
                { key: 'sold', label: lang === 'es' ? 'Vendido' : 'Sold', color: statusColors.sold },
              ].map(item => (
                <div key={item.key} className="flex items-center gap-1.5" data-testid={`legend-mobile-${item.key}`}>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-[9px] text-white/35 tracking-[0.08em] uppercase">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom sheet */}
          <AnimatePresence>
            {activeFloor && activeData.length > 0 && (
              <motion.div
                key={`mobile-info-${activeFloor}`}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="flex-shrink-0 border-t border-white/10 bg-black/80 backdrop-blur-xl max-h-[45vh] overflow-y-auto"
                data-testid={`floor-info-mobile-${activeFloor}`}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-light gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {lang === 'es' ? 'Piso' : 'Floor'} {activeFloor}
                      </h3>
                      <p className="text-[10px] text-white/40">{activeData.length} {activeData.length > 1 ? (lang === 'es' ? 'unidades' : 'units') : (lang === 'es' ? 'unidad' : 'unit')}</p>
                    </div>
                    <button
                      onClick={() => setSelectedFloor(null)}
                      className="text-white/40 hover:text-white text-xs px-3 py-1 border border-white/10"
                    >
                      ✕
                    </button>
                  </div>

                  {activeData.map((apt) => (
                    <div key={apt.id} className="py-3 border-t border-white/8">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white/90" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {apt.unit}
                        </span>
                        <span className="text-[9px] tracking-[0.06em] uppercase px-2 py-0.5"
                          style={{ color: statusColors[apt.status], background: statusBg[apt.status] }}>
                          {statusLabel(apt.status)}
                        </span>
                      </div>
                      <div className="flex gap-4 text-[10px] text-white/50 mb-2">
                        <span>{apt.area_m2} m²</span>
                        <span>{apt.bedrooms} {lang === 'es' ? 'hab' : 'bed'}</span>
                        <span>{apt.bathrooms} {lang === 'es' ? 'baños' : 'bath'}</span>
                      </div>
                      <span className="text-base font-light gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {formatPrice(apt.price)}
                      </span>
                    </div>
                  ))}

                  <button
                    onClick={() => navigate(`/tower/${towerId}/floor/${activeFloor}`)}
                    data-testid={`view-floor-mobile-${activeFloor}`}
                    className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-white py-3 mt-3 uppercase tracking-[0.1em] text-xs font-light hover:bg-[#B5952F] transition-colors"
                  >
                    {lang === 'es' ? 'Ver Detalles' : 'View Details'}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="relative z-0" style={{ height: '100vh' }} />
      <div ref={explorerRef} className="relative z-0" style={{ height: '200vh' }} />
    </div>
  );
}


function InfoPanel({ towerId, activeFloor, activeData, lang, statusColors, statusBg, statusLabel, formatPrice, navigate }) {
  return (
    <AnimatePresence mode="wait">
      {activeFloor && activeData.length > 0 ? (
        <motion.div
          key={`info-${activeFloor}`}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 24 }}
          transition={{ duration: 0.22 }}
          data-testid={`floor-info-${activeFloor}`}
        >
          <div className="border border-white/10 bg-black/60 backdrop-blur-xl shadow-2xl max-h-[calc(100vh-220px)] overflow-y-auto">
            <div className="p-6 border-b border-white/10 sticky top-0 bg-black/80 backdrop-blur-xl z-10">
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {activeFloor >= 9 ? 'Penthouse' : (lang === 'es' ? 'Piso' : 'Floor')}
              </p>
              <h3 className="text-3xl font-light tracking-tight gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {lang === 'es' ? 'Piso' : 'Floor'} {activeFloor}
              </h3>
              <p className="text-[11px] text-white/40 mt-1">
                {activeData.length} {activeData.length > 1 ? (lang === 'es' ? 'unidades' : 'units') : (lang === 'es' ? 'unidad' : 'unit')}
              </p>
            </div>
            {activeData.map((apt) => (
              <div key={apt.id} className="p-5 border-b border-white/8 last:border-b-0">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-white/90" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {apt.unit}
                  </span>
                  <span className="text-[10px] tracking-[0.08em] uppercase px-2.5 py-1 font-medium"
                    style={{ color: statusColors[apt.status], background: statusBg[apt.status] }}>
                    {statusLabel(apt.status)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-[11px] text-white/50 mb-3">
                  <div className="flex items-center gap-1.5">
                    <Maximize size={12} className="text-[#D4AF37]" /><span>{apt.area_m2} m²</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Bed size={12} className="text-[#D4AF37]" /><span>{apt.bedrooms}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Bath size={12} className="text-[#D4AF37]" /><span>{apt.bathrooms}</span>
                  </div>
                </div>
                <span className="text-lg font-light gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {formatPrice(apt.price)}
                </span>
              </div>
            ))}
            <div className="p-4 sticky bottom-0 bg-black/80 backdrop-blur-xl">
              <button
                onClick={() => navigate(`/tower/${towerId}/floor/${activeFloor}`)}
                data-testid={`view-floor-${activeFloor}`}
                className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-white py-3.5 uppercase tracking-[0.12em] text-xs font-light hover:bg-[#B5952F] transition-colors duration-300"
              >
                {lang === 'es' ? 'Ver Detalles' : 'View Details'}
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="border border-dashed border-white/10 bg-black/30 backdrop-blur-sm p-8">
            <div className="w-10 h-10 mb-4 border border-[#D4AF37]/30 flex items-center justify-center">
              <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
            </div>
            <p className="text-sm text-white/50 font-light leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {lang === 'es'
                ? 'Seleccione un piso en el edificio para ver los detalles'
                : 'Select a floor on the building to view details'}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
